import React, { useState, useEffect, useRef } from 'react';
import reportingFinancierApi from '../services/reporting-financier';
import useReportingData from '../hooks/useReportingData';
import UpdatingOverlay from './UpdatingOverlay';
import './ReportingGlobal.css';

// Reporting Financier — vue Global, style "wallboard".
//
// Structure :
//   1. Bandeau "Performance générale" : CA total HT en typographie XXL,
//      delta N-1 et valeur N-1 inline. Pas de carte, juste de la typo.
//   2. "Ledger" : grille horizontale des services branchés (APV / PDR / TRX),
//      chaque cellule = un service avec son KPI principal en XL et 2 sous-KPIs.
//      Les services en construction (VN/VO/ADMIN) apparaissent en pied de page
//      en une seule ligne discrète.
//   3. Mobile : la même grille devient un carrousel scroll-snap (1 service /
//      écran), avec dots indicateurs en bas et tap pour drill-down.
//
// Pas de cartes (ni grille de cartes), pas de side-stripes, pas de gradient.
// Typographie large + alignement strict = lisibilité salle de pilotage.

// ─── Helpers ──────────────────────────────────────────────────────────────
const fmtMoney = (v) => v == null
  ? '—'
  : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v);

// Formate un montant en "K€" ou "M€" pour les cellules services (compact).
function fmtMoneyCompact(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) {
    const n = Math.round(v / 100_000) / 10;
    return `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(n)} M€`;
  }
  if (abs >= 1_000) {
    const n = Math.round(v / 1_000);
    return `${new Intl.NumberFormat('fr-FR').format(n)} K€`;
  }
  return fmtMoney(v);
}

const fmtNumber = (v) => v == null ? '—' : new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
const fmtDelta  = (pct) => pct == null ? null : `${pct > 0 ? '+' : ''}${pct.toFixed(1)} %`;

function DeltaText({ pct, size = 'md' }) {
  if (pct == null) return null;
  const cls = pct > 0 ? 'rg-up' : pct < 0 ? 'rg-down' : 'rg-neutral';
  const arrow = pct > 0 ? '▲' : pct < 0 ? '▼' : '◆';
  return <span className={`rg-delta rg-delta-${size} ${cls}`}>{arrow} {fmtDelta(pct)}</span>;
}

// Sous-KPI(s) pour une cellule service. Format compact.
function subKpiLine(s) {
  const sk = s.sub_kpis || {};
  const parts = [];
  // VN/VO : volume + marge + taux de marge
  if (sk.volume != null) {
    parts.push(`${fmtNumber(sk.volume)} ventes`);
    if (sk.marge != null) parts.push(`marge ${fmtMoneyCompact(sk.marge)}`);
    if (sk.taux_marge != null) parts.push(`${sk.taux_marge.toFixed(1)} %`);
    return parts.join(' · ');
  }
  // APV/PDR/TRX : factures + ticket moyen
  if (sk.nb_factures != null && sk.nb_factures > 0) {
    parts.push(`${fmtNumber(sk.nb_factures)} factures`);
  }
  if (sk.ticket_moyen != null && sk.ticket_moyen > 0) {
    parts.push(`ticket ${fmtMoney(sk.ticket_moyen)}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

// Format de la période en français lisible (ex : "Mai 2026" si un seul mois,
// sinon "01/05 → 26/05/2026").
function formatPeriode(date_debut, date_fin) {
  const d1 = new Date(date_debut);
  const d2 = new Date(date_fin);
  const sameMonth = d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth();
  const sameYear  = d1.getFullYear() === d2.getFullYear();
  if (sameMonth) {
    return d1.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      .replace(/^./, c => c.toUpperCase());
  }
  const opts = { day: '2-digit', month: '2-digit' };
  return `${d1.toLocaleDateString('fr-FR', opts)} → ${d2.toLocaleDateString('fr-FR', sameYear ? opts : { ...opts, year: 'numeric' })}`;
}

// ─── Cellule service ──────────────────────────────────────────────────────
// Cliquable → navigue vers l'onglet du service. Variante "wip" en grisé.
function ServiceCell({ service, onClick }) {
  const isWip = service.statut === 'wip';
  return (
    <button
      type="button"
      className={`rg-cell ${isWip ? 'rg-cell-wip' : ''}`}
      onClick={isWip ? undefined : onClick}
      disabled={isWip}
      aria-label={`${service.label} — ouvrir l'onglet`}
    >
      <div className="rg-cell-code">{service.code}</div>
      {isWip ? (
        <div className="rg-cell-wip-msg">En construction</div>
      ) : (
        <>
          <div className="rg-cell-value">{fmtMoneyCompact(service.ca)}</div>
          <div className="rg-cell-delta-row">
            <DeltaText pct={service.delta_ca_pct} size="md" />
          </div>
          <div className="rg-cell-sub">{subKpiLine(service) || '—'}</div>
        </>
      )}
    </button>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────
function ReportingGlobal({ dateDebut, dateFin, concessions, onNavigateToService }) {
  const { data: overview, loading, error } = useReportingData(
    (signal) => reportingFinancierApi.getGlobalOverview(
      { date_debut: dateDebut, date_fin: dateFin, ...(concessions?.length ? { concession: concessions } : {}) },
      signal,
    ).then(r => r.data),
    [dateDebut, dateFin, JSON.stringify(concessions || [])],
    { enabled: !!(dateDebut && dateFin) },
  );

  // Tracking de la position du carrousel mobile pour les dots.
  const ledgerRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const el = ledgerRef.current;
    if (!el || !overview) return;
    // Observer chaque cellule pour détecter laquelle est centrée à l'écran.
    const cells = Array.from(el.querySelectorAll('.rg-cell'));
    if (cells.length === 0) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
          const i = cells.indexOf(entry.target);
          if (i >= 0) setCurrentSlide(i);
        }
      });
    }, { root: el, threshold: [0.55, 0.8] });
    cells.forEach(c => observer.observe(c));
    return () => observer.disconnect();
  }, [overview]);

  if (loading && !overview) {
    return <div className="rg-loading">Chargement…</div>;
  }
  if (error && !overview) {
    return <div className="rg-error">⚠️ {error}</div>;
  }
  if (!overview) return null;

  const { total, services, periode, marche, stock_vo, stock_vn } = overview;
  const connected = services.filter(s => s.statut === 'connected');
  const wip       = services.filter(s => s.statut === 'wip');
  const periodLabel = formatPeriode(periode.date_debut, periode.date_fin);
  const concLabel = concessions?.length
    ? (concessions.length === 1 ? concessions[0] : `${concessions.length} concessions`)
    : 'Toutes concessions';

  return (
    <div className="rg-page rf-surface">
      <UpdatingOverlay show={loading} />
      {/* ─── 1. Headline ─────────────────────────────────────────────── */}
      <header className="rg-headline">
        <div className="rg-headline-context">
          Performance générale · {periodLabel} · {concLabel}
        </div>
        <div className="rg-headline-value">{fmtMoney(total.ca)}</div>
        <div className="rg-headline-meta">
          <DeltaText pct={total.delta_ca_pct} size="lg" />
          {total.ca_n1 != null && (
            <span className="rg-headline-n1">vs {fmtMoney(total.ca_n1)} en N-1</span>
          )}
        </div>
      </header>

      {/* ─── 2. Ledger / carrousel mobile — services branchés uniquement ── */}
      <div className="rg-ledger" ref={ledgerRef}>
        {connected.map(s => (
          <ServiceCell
            key={s.code}
            service={s}
            onClick={() => onNavigateToService?.(s.code)}
          />
        ))}
      </div>

      {/* Dots indicateurs — visibles uniquement sur mobile (CSS) */}
      {connected.length > 1 && (
        <div className="rg-dots" aria-hidden="true">
          {connected.map((s, i) => (
            <span
              key={s.code}
              className={`rg-dot ${i === currentSlide ? 'rg-dot-active' : ''}`}
            />
          ))}
        </div>
      )}

      {/* ─── 3. Marché par département ──────────────────────────────── */}
      {marche && marche.length > 0 && (
        <div className="rg-marche-section">
          <div className="rg-marche-title">📈 Marché — Immatriculations</div>
          <div className="rg-marche-grid">
            {marche.map(m => (
              <button
                key={m.departement}
                type="button"
                className="rg-cell rg-marche-cell"
                onClick={() => onNavigateToService?.('marche')}
              >
                <div className="rg-cell-code">{m.departement.charAt(0) + m.departement.slice(1).toLowerCase()}</div>
                <div className="rg-cell-value">{fmtNumber(m.volume)} <span className="rg-marche-unit">immat.</span></div>
                <div className="rg-cell-delta-row">
                  <DeltaText pct={m.delta_volume_pct} size="md" />
                  {m.volume_n1 != null && (
                    <span className="rg-marche-n1">vs {fmtNumber(m.volume_n1)} N-1</span>
                  )}
                </div>
                <div className="rg-cell-sub">
                  {m.marque_leader && <>Leader : {m.marque_leader.marque} ({m.marque_leader.part} %)</>}
                  {m.notre_part && (
                    <span className="rg-marche-notre-part"> · Nos marques : <strong>{m.notre_part.part} %</strong></span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── 4. Stock VN (gauche) + Stock VO (droite) ─────────────────── */}
      {((stock_vn && stock_vn.total > 0) || (stock_vo && stock_vo.total > 0)) && (
        <div className="rg-stock-row">
          {stock_vn && stock_vn.total > 0 && (
            <div className="rg-marche-section">
              <div className="rg-marche-title">📦 Stock VN</div>
              <div className="rg-marche-grid">
                <button type="button" className="rg-cell" onClick={() => onNavigateToService?.('stockvn')}>
                  <div className="rg-cell-code">Véhicules</div>
                  <div className="rg-cell-value">{fmtNumber(stock_vn.total)}</div>
                  <div className="rg-cell-sub">{stock_vn.nb_marques} marques · âge moy. {stock_vn.age_moyen_mois} mois</div>
                </button>
                {stock_vn.dormant_nb > 0 && (
                  <button type="button" className="rg-cell rg-stock-dormant" onClick={() => onNavigateToService?.('stockvn')}>
                    <div className="rg-cell-code">Dormants (&gt;120j)</div>
                    <div className="rg-cell-value">{fmtNumber(stock_vn.dormant_nb)}</div>
                    <div className="rg-cell-sub">{stock_vn.dormant_pct} % du stock</div>
                  </button>
                )}
              </div>
            </div>
          )}
          {stock_vo && stock_vo.total > 0 && (
            <div className="rg-marche-section">
              <div className="rg-marche-title">📦 Stock VO</div>
              <div className="rg-marche-grid">
                <button type="button" className="rg-cell" onClick={() => onNavigateToService?.('stockvo')}>
                  <div className="rg-cell-code">Véhicules</div>
                  <div className="rg-cell-value">{fmtNumber(stock_vo.total)}</div>
                  <div className="rg-cell-sub">{stock_vo.nb_marques} marques · âge moy. {stock_vo.age_moyen_mois} mois</div>
                </button>
                {stock_vo.dormant_nb > 0 && (
                  <button type="button" className="rg-cell rg-stock-dormant" onClick={() => onNavigateToService?.('stockvo')}>
                    <div className="rg-cell-code">Dormants (&gt;120j)</div>
                    <div className="rg-cell-value">{fmtNumber(stock_vo.dormant_nb)}</div>
                    <div className="rg-cell-sub">{stock_vo.dormant_pct} % du stock</div>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── 5. Pied : services WIP, en discret ───────────────────────── */}
      {wip.length > 0 && (
        <footer className="rg-wip-footer">
          À venir : {wip.map(s => s.code).join(' · ')}
        </footer>
      )}

      {/* Marqueur invisible pour aider les tests : nombre de services branchés */}
      <span className="sr-only" data-testid="rg-connected-count">{connected.length}</span>
    </div>
  );
}

export default ReportingGlobal;
